import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, Tag } from "lucide-react";
import usePageTitle from "@/lib/usePageTitle";
import useOpenGraph from "@/lib/useOpenGraph";
import { coverForArticle, DEFAULT_ARTICLE_COVER } from "@/lib/articleCovers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(d);
  }
};

export default function HealthNotesPage() {
  usePageTitle("Health Notes");
  useOpenGraph({
    title: "Health Notes · Mumbai Elite Diagnostics",
    description:
      "Field notes for everyday patients — preventive health, understanding lab reports, and questions to ask your doctor — by Dr. Anubhuti Jain.",
    url: "https://mumbaielitediagnostics.com/health-notes",
    image: DEFAULT_ARTICLE_COVER,
    type: "website",
    imageAlt: "Mumbai Elite Diagnostics | Health Notes & Medical Guides",
  });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section
      data-testid="health-notes-page"
      className="bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24 border-b border-[#E2E8F0]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-4">
              Health Notes · By Dr. Anubhuti Jain
            </div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A2540]">
              Field notes for the
              <br />
              <span className="italic text-[#2E5AA5]">everyday patient</span>.
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="font-sans text-base text-[#475569] leading-relaxed">
              Short, practical writing on preventive health, understanding your
              lab reports, and questions to ask your doctor — from a
              consultant pathologist.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-24">
        {loading && (
          <div
            data-testid="health-notes-loading"
            className="py-20 text-center font-mono text-xs uppercase tracking-widest-plus text-[#94A3B8]"
          >
            Loading…
          </div>
        )}

        {!loading && articles.length === 0 && (
          <div
            data-testid="health-notes-empty"
            className="py-24 text-center border border-dashed border-[#E2E8F0]"
          >
            <p className="font-serif italic text-2xl text-[#94A3B8]">
              First article is being written. Check back soon.
            </p>
          </div>
        )}

        {!loading && articles.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-[#E2E8F0]">
            {articles.map((a, i) => (
              <motion.article
                key={a.id}
                data-testid={`article-card-${a.slug}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="group border-r border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
              >
                <Link to={`/health-notes/${a.slug}`} className="block h-full">
                  <div className="aspect-[16/9] w-full overflow-hidden bg-[#F1F5F9]">
                    <img
                      src={coverForArticle(a)}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-8 lg:p-10 flex flex-col h-full">
                    <div className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8] mb-4">
                      <Calendar size={12} strokeWidth={1.5} />
                      {fmtDate(a.created_at)}
                    </div>
                    <h2 className="font-serif text-2xl lg:text-3xl text-[#0A2540] leading-tight group-hover:text-[#2E5AA5] transition-colors mb-3">
                      {a.title}
                    </h2>
                    <p className="font-sans text-sm text-[#475569] leading-relaxed line-clamp-3">
                      {a.excerpt}
                    </p>
                    <div className="mt-6 pt-4 border-t border-[#E2E8F0] flex items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        {a.tags.slice(0, 2).map((t) => (
                          <span
                            key={t}
                            className="font-mono text-[9px] uppercase tracking-widest-plus text-[#475569] border border-[#E2E8F0] px-2 py-0.5"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest-plus text-[#0A2540] group-hover:text-[#B08A4A] transition-colors">
                        Read
                        <ArrowUpRight
                          size={12}
                          strokeWidth={1.5}
                          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
